import { z } from "zod";
/**
 * FIRST-PARTY VISIT COUNTING (CME analytics, stage A; ADR 0022): THE BEACON
 * (`POST /a/v`), what the web client may send about one route view. A route
 * *name* from a closed list (never an id, never a query), the referring
 * site's domain on the landing view only, and the three campaign tags from
 * the landing URL. Nothing else: the strict objects refuse any other field,
 * so nothing else can be stored.
 *
 * Only the beacon's half lives here (ADR 0024). This module is re-exported
 * from the package root, so the public web app's bundle, and the source map
 * served beside it, carry every line of it. The operator dashboard's half is
 * ./analytics-dashboard.ts, on its own package subpath and never re-exported
 * from the root.
 */
/**
 * Every route the web client can count: apps/web's whole route table (the
 * operator's own area was never counted, and has left the public app). A
 * hash the table does not know reads as `landing` there, so it is counted as
 * `landing` here. The web holds its table equal to this list in a test.
 */
export const ANALYTICS_ROUTES = [
    "landing",
    "scenarios",
    "auth",
    "privacy",
    "terms",
    "onboarding",
    "home",
    "beat",
    "play",
    "recommend",
    "debrief",
    "audit",
    "profile",
    "coach",
    "library",
    "book",
    "skills",
    "gallery",
    "stage",
    "play-review",
    "front-door-review",
];
export const AnalyticsRouteSchema = z.enum(ANALYTICS_ROUTES);
/** The longest a campaign tag may be, in the beacon, the database and the CME's link helper alike. */
export const UTM_MAX_LENGTH = 60;
/** Letters, numbers, spaces, hyphens and underscores (Codex's rule for the link helper, held everywhere). */
export const UTM_PATTERN = /^[A-Za-z0-9 _-]+$/;
/** One campaign tag: trimmed, 1–60 of the allowed characters. */
export const UtmValueSchema = z.string().trim().min(1).max(UTM_MAX_LENGTH).regex(UTM_PATTERN);
/**
 * A registrable host name only (`news.example.org`): never a path, a query or
 * a port, and never an IP address. Two or more labels, the last a letter-only
 * or punycode top-level domain, so `198.51.100.77` (digits last), any IPv6
 * literal (colons, brackets) and `localhost` (one label) all fail. The
 * database's check on `analytics_hits.referrer_domain` is the same pattern.
 */
export const REFERRER_DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
export const ReferrerDomainSchema = z.string().max(253).regex(REFERRER_DOMAIN_PATTERN);
/** The beacon's whole body. Strict: one unknown field and the request is refused. */
export const VisitBeaconSchema = z.strictObject({
    route: AnalyticsRouteSchema,
    referrer: ReferrerDomainSchema.optional(),
    utmSource: UtmValueSchema.optional(),
    utmMedium: UtmValueSchema.optional(),
    utmCampaign: UtmValueSchema.optional(),
});
/** The coarse device class the server derives from the user agent; the user agent itself is never stored. */
export const DeviceClassSchema = z.enum(["PHONE", "TABLET", "COMPUTER", "OTHER"]);
//# sourceMappingURL=analytics.js.map